import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, makeStyles, TextField } from "@material-ui/core";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import DefaultValuesUtils from "../../utils/classes/DefaultValuesUtils";
import toastError from "../../errors/toastError";

import { useGlobalRegisters } from "../../context/GlobalRegisters/GlobalRegistersContext";

const useStyles = makeStyles((theme) => ({
  maxWidth: { width: "100%", padding: "0 0 20px 0", },

	floatingButton: {
    transition: "transform 0.3s",
    "&:hover": { transform: "translateY(-5px)", },
  }
}));

const QueueOverrideRequestAuthorizationModal = ({ open, onClose, ticketId, contactId }) => {
	// 	***************
	// 	** Variables **
	// 	***************
	const classes = useStyles();
	const requestReasonRef = useRef(null);

	const [loading, setLoading] = useState(false);
	const [requestReason, setRequestReason] = useState("");

	const { queueOverridesDispatch } = useGlobalRegisters();



	// 	***************
	// 	** Functions **
	// 	***************
	const handleClose = () => {
		onClose();
		setRequestReason("");
	};

	const handleSubmitFormByKeys = (event) => {
		if (event.ctrlKey && event.key === "Enter") {
			event.preventDefault();
			
			const submitButton = document.getElementById("queueOverrideSubmitButton");
			submitButton?.click();
		}
	};

	const handleRequestReasonChange = (event) => {
    setRequestReason(event.target.value);
  };

  const handleSaveAuthorizationRequest = async (event) => {
    event.preventDefault();

    try {
      setLoading(true);

      if (!requestReason.trim()) {
        toast.info(i18n.t("queueOverrideRequestAuthorizationModal.validations.requestReason"));
        return;
      }

			if (requestReason.trim().length < DefaultValuesUtils.TEXT_SIZES.queueOverrideReason.min) {
				toast.info(
					i18n.t("queueOverrideRequestAuthorizationModal.validations.requestReasonLength")
						.replace("<size>", DefaultValuesUtils.TEXT_SIZES.queueOverrideReason.min)
				);
				return;
			}

      const { data } = await api.post("/queueOverrides", { ticketId, contactId, requestReason });
			
			if (data.success) {
				toast.success(i18n.t("queueOverrideRequestAuthorizationModal.toasts.success"));
				queueOverridesDispatch({ type: "UPDATE_QUEUE_OVERRIDE", payload: data.queueOverride });
				handleClose();
			}
			else {
				toast.info(i18n.t(data.message));
			}
    }
    catch (exception) {
			console.log("handleSaveAuthorizationRequest exception:", exception);
      toastError(exception);
    }
    finally {
      setLoading(false);
    }
  };



	// 	************
	// 	** Return **
	// 	************
	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth="lg"
			scroll="paper"
			TransitionProps={{ onEntered: () => requestReasonRef.current?.focus() }}
		>
			<form onSubmit={async (event) => await handleSaveAuthorizationRequest(event)}>
				<DialogTitle>
					{i18n.t("queueOverrideRequestAuthorizationModal.title")}
				</DialogTitle>

				<DialogContent dividers>
					<TextField
						inputRef={requestReasonRef}
						value={requestReason}
						as={TextField}
						label={`${i18n.t("queueOverrideRequestAuthorizationModal.form.requestReason")} ${requestReason.length}/${DefaultValuesUtils.TEXT_SIZES.queueOverrideReason.max}`}
						autoFocus={true}
						name="requestReason"
						variant="outlined"
						margin="dense"
						fullWidth
						className={classes.textField}
						multiline={true}
						minRows={5}
						maxRows={5}
						onChange={handleRequestReasonChange}
						onKeyDown={handleSubmitFormByKeys}
            inputProps={{
							minLength: DefaultValuesUtils.TEXT_SIZES.queueOverrideReason.min
							, maxLength: DefaultValuesUtils.TEXT_SIZES.queueOverrideReason.max
						}}
					/>
				</DialogContent>

				<DialogActions>
					<Button
						onClick={handleClose}
						color="inherit"
						disabled={loading}
						variant="outlined"
						className={classes.floatingButton}
					>
						{i18n.t("queueOverrideRequestAuthorizationModal.buttons.cancel")}
					</Button>

					<ButtonWithSpinner
						id="queueOverrideSubmitButton"
						variant="contained"
						type="submit"
						color="primary"
						loading={loading}
						className={classes.floatingButton}
					>
						{i18n.t("queueOverrideRequestAuthorizationModal.buttons.ok")}
					</ButtonWithSpinner>
				</DialogActions>
			</form>
		</Dialog>
	);
};

export default QueueOverrideRequestAuthorizationModal;
